import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Checkbox,
  FormControlLabel,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Aviso } from '../../types/avisos';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import GuiaFormatacao from './GuiaFormatacao';
import AnexosAviso from './AnexosAviso';
import BarraFormatacao from '../common/BarraFormatacao';

interface NovoAvisoFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (aviso: Omit<Aviso, 'id' | 'data_criacao'>) => Promise<void>;
  avisoParaEditar?: Aviso;
}

const NovoAvisoForm: React.FC<NovoAvisoFormProps> = ({
  open,
  onClose,
  onSubmit,
  avisoParaEditar
}) => {
  const { usuario } = useAuth();
  const { departamentos } = useDepartamento();
  const { usuarios } = useUsuario();
  
  const [titulo, setTitulo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [tipo, setTipo] = useState<'geral' | 'departamento' | 'usuario'>('geral');
  const [departamentoId, setDepartamentoId] = useState<string | null>(null);
  const [destinatarios, setDestinatarios] = useState<string[]>([]);
  const [fixado, setFixado] = useState(false);
  const [mostrarGuia, setMostrarGuia] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [mostrarPreview, setMostrarPreview] = useState(false);

  // Preencher o formulário se estiver editando um aviso existente
  useEffect(() => {
    if (avisoParaEditar) {
      setTitulo(avisoParaEditar.titulo);
      setConteudo(avisoParaEditar.conteudo);
      setTipo(avisoParaEditar.tipo);
      setDepartamentoId(avisoParaEditar.departamento_id);
      setFixado(avisoParaEditar.fixado);
      
      if (avisoParaEditar.destinatarios) {
        setDestinatarios(avisoParaEditar.destinatarios.split(','));
      } else {
        setDestinatarios([]);
      }
    } else {
      // Valores padrão para novo aviso
      resetForm();
    }
  }, [avisoParaEditar, open]);

  const resetForm = () => {
    setTitulo('');
    setConteudo('');
    setTipo('geral');
    setDepartamentoId(null);
    setDestinatarios([]);
    setFixado(false);
    setErros({});
  };

  const validarFormulario = (): boolean => {
    const novosErros: Record<string, string> = {};
    
    if (!titulo.trim()) {
      novosErros.titulo = 'O título é obrigatório';
    }
    
    if (!conteudo.trim()) {
      novosErros.conteudo = 'O conteúdo é obrigatório';
    }
    
    if (tipo === 'departamento' && !departamentoId) {
      novosErros.departamentoId = 'Selecione um departamento';
    }
    
    if (tipo === 'usuario' && destinatarios.length === 0) {
      novosErros.destinatarios = 'Selecione pelo menos um usuário';
    }
    
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      return;
    }
    
    setEnviando(true);
    
    try {
      const novoAviso: Omit<Aviso, 'id' | 'data_criacao'> = {
        titulo,
        conteudo,
        usuario_id: usuario!.id,
        tipo,
        destinatarios: tipo === 'usuario' ? destinatarios.join(',') : null,
        departamento_id: tipo === 'departamento' ? departamentoId : null,
        fixado,
        ativo: true
      };
      
      await onSubmit(novoAviso);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Erro ao salvar aviso:', error);
    } finally {
      setEnviando(false);
    }
  };

  const handleTipoChange = (e: SelectChangeEvent<string>) => {
    const novoTipo = e.target.value as 'geral' | 'departamento' | 'usuario';
    setTipo(novoTipo);
    
    // Resetar campos relacionados
    if (novoTipo !== 'departamento') {
      setDepartamentoId(null);
    }
    
    if (novoTipo !== 'usuario') {
      setDestinatarios([]);
    }
  };

  const handleDestinatariosChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setDestinatarios(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      aria-labelledby="form-aviso-titulo"
    >
      <DialogTitle id="form-aviso-titulo">
        {avisoParaEditar ? 'Editar Aviso' : 'Novo Aviso'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="titulo"
            label="Título"
            type="text"
            fullWidth
            variant="outlined"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            error={!!erros.titulo}
            helperText={erros.titulo}
            required
          />
          
          <Box sx={{ mt: 1, mb: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Conteúdo *
              </Typography>
              <Tooltip title="Ver guia completo de formatação">
                <IconButton onClick={() => setMostrarGuia(true)} size="small" color="primary">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <BarraFormatacao
              valor={conteudo}
              onChange={setConteudo}
              placeholder="Digite o conteúdo do aviso..."
              minHeight={150}
              error={!!erros.conteudo}
              helperText={erros.conteudo}
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Button
              size="small"
              onClick={() => setMostrarGuia(true)}
              startIcon={<HelpOutlineIcon />}
              color="info"
            >
              Como usar imagens e links?
            </Button>
            <Button
              size="small"
              onClick={() => setMostrarPreview(!mostrarPreview)}
              color="primary"
            >
              {mostrarPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
            </Button>
          </Box>
          
          {mostrarPreview && conteudo && (
            <Box 
              sx={{ 
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'action.hover',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 1,
                  mb: 1,
                  fontWeight: 'bold',
                  lineHeight: 1.2
                },
                '& h1': { fontSize: '1.4rem' },
                '& h2': { fontSize: '1.3rem' },
                '& h3': { fontSize: '1.2rem' },
                '& h4': { fontSize: '1.1rem' },
                '& h5, & h6': { fontSize: '1rem' },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 2, mb: 1 },
                '& a': { color: 'primary.main', textDecoration: 'underline' },
                '& blockquote': { 
                  borderLeft: '3px solid #ccc', 
                  pl: 1, 
                  ml: 0,
                  fontStyle: 'italic' 
                },
                '& code': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontFamily: 'monospace'
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto'
                }
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                Preview:
              </Typography>
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>{conteudo}</ReactMarkdown>
            </Box>
          )}
          
          <Box sx={{ mt: 1, mb: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" component="div">
                <strong>Dicas de formatação:</strong>
              </Typography>
              <Button 
                size="small" 
                variant="text" 
                color="primary"
                onClick={() => {
                  const exemploMarkdown = `# Título Principal

## Subtítulo

Este é um **texto em negrito** e este é um *texto em itálico*.

### Lista de itens:
- Item 1
- Item 2
- Item 3

> Este é um bloco de citação.

[Link para o site](https://exemplo.com)`;
                  setConteudo(exemploMarkdown);
                }}
              >
                Inserir Exemplo
              </Button>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li><code>**texto**</code> para <strong>negrito</strong></li>
              <li><code>*texto*</code> para <em>itálico</em></li>
              <li><code># Título</code> para títulos</li>
              <li><code>- item</code> para listas</li>
              <li><code>[link](url)</code> para links</li>
              <li><code>{'>'} texto</code> para citações</li>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="tipo-label">Tipo de Aviso</InputLabel>
              <Select
                labelId="tipo-label"
                id="tipo"
                value={tipo}
                onChange={handleTipoChange}
                label="Tipo de Aviso"
              >
                <MenuItem value="geral">Geral (Todos os usuários)</MenuItem>
                <MenuItem value="departamento">Departamento específico</MenuItem>
                <MenuItem value="usuario">Usuários específicos</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {tipo === 'departamento' && (
            <FormControl 
              fullWidth 
              variant="outlined" 
              margin="dense"
              error={!!erros.departamentoId}
            >
              <InputLabel id="departamento-label">Departamento</InputLabel>
              <Select
                labelId="departamento-label"
                id="departamento"
                value={departamentoId || ''}
                onChange={(e) => setDepartamentoId(e.target.value)}
                label="Departamento"
              >
                {departamentos.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.nome}
                  </MenuItem>
                ))}
              </Select>
              {erros.departamentoId && (
                <FormHelperText>{erros.departamentoId}</FormHelperText>
              )}
            </FormControl>
          )}
          
          {tipo === 'usuario' && (
            <FormControl 
              fullWidth 
              variant="outlined" 
              margin="dense"
              error={!!erros.destinatarios}
            >
              <InputLabel id="destinatarios-label">Destinatários</InputLabel>
              <Select
                labelId="destinatarios-label"
                id="destinatarios"
                multiple
                value={destinatarios}
                onChange={handleDestinatariosChange}
                input={<OutlinedInput id="select-destinatarios" label="Destinatários" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const user = usuarios.find(u => u.id === value);
                      return (
                        <Chip key={value} label={user?.nome || value} />
                      );
                    })}
                  </Box>
                )}
              >
                {usuarios.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.nome}
                  </MenuItem>
                ))}
              </Select>
              {erros.destinatarios && (
                <FormHelperText>{erros.destinatarios}</FormHelperText>
              )}
            </FormControl>
          )}
          
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fixado}
                  onChange={(e) => setFixado(e.target.checked)}
                  name="fixado"
                  color="primary"
                />
              }
              label="Fixar este aviso (aparecerá no topo da lista)"
            />
          </Box>

          {/* Anexos (só para avisos já criados) */}
          {avisoParaEditar && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Anexos
              </Typography>
              <AnexosAviso
                avisoId={avisoParaEditar.id}
                podeAdicionar={usuario?.tipo === 'admin'}
                podeExcluir={usuario?.tipo === 'admin'}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={enviando}
          >
            {enviando ? 'Salvando...' : avisoParaEditar ? 'Atualizar' : 'Publicar'}
          </Button>
        </DialogActions>
      </form>
      
      {/* Guia de Formatação */}
      <GuiaFormatacao 
        aberto={mostrarGuia} 
        onFechar={() => setMostrarGuia(false)} 
      />
    </Dialog>
  );
};

export default NovoAvisoForm;
